import { Download } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type ReactElement } from 'react';

import {
  focusMenuItem,
  menuButtonStart,
  onMenuKeyDown,
  type MenuStart,
} from '@/components/ui/menu-keys';
import { useDismiss } from '@/components/ui/use-dismiss';
import { useExportReport, type ExportFormat } from '@/components/ui/use-export-report';

// "Exportar" de los listados de F1 (spec 2026-10-14 §3 D1/D6): un botón con menú CSV /
// PDF en la barra de la pantalla; exporta lo filtrado completo (`query` son los filtros
// de la URL, sin paginación). Mismo patrón de menú que el de la cuenta (WAI-ARIA).

const OPTIONS: { format: ExportFormat; label: string }[] = [
  { format: 'csv', label: 'CSV para Excel' },
  { format: 'pdf', label: 'PDF' },
];

export function ExportMenu({
  path,
  query,
  title,
  compact = false,
}: {
  /** Endpoint `/export` del listado. */
  path: string;
  /** Filtros vigentes como query string (sin `?`, sin página). */
  query: string;
  /** Nombre del informe para el aviso ("Informe de Lista Maestra descargado en CSV"). */
  title: string;
  /** Botón chico, para la cabecera de una tarjeta (como sus otras acciones). */
  compact?: boolean;
}): ReactElement {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<MenuStart>('first');
  const { run, pending } = useExportReport(path, query, title);

  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, [wrapRef], buttonRef);

  const busy = pending !== null;

  useEffect(() => {
    if (open) {
      focusMenuItem(menuRef.current, startRef.current);
    }
  }, [open]);

  return (
    <div className="export-menu" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={compact ? 'btn mut sm' : 'btn mut'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-busy={busy}
        // aria-disabled, no disabled: un botón deshabilitado suelta el foco al body y
        // quien usa teclado pierde su lugar mientras se descarga el informe.
        aria-disabled={busy}
        onClick={() => {
          if (!busy) {
            startRef.current = 'first';
            setOpen((o) => !o);
          }
        }}
        onKeyDown={(e) => {
          const start = busy ? null : menuButtonStart(e);
          if (start !== null) {
            startRef.current = start;
            if (open) {
              focusMenuItem(menuRef.current, start);
            } else {
              setOpen(true);
            }
          }
        }}
      >
        <Download aria-hidden="true" size={compact ? 14 : undefined} strokeWidth={1.9} />
        {busy ? 'Exportando…' : 'Exportar'}
      </button>
      {open && (
        <div
          id={menuId}
          ref={menuRef}
          className="pop-menu"
          role="menu"
          tabIndex={-1}
          aria-label={`Exportar ${title}`}
          onKeyDown={(e) => onMenuKeyDown(e, close)}
        >
          {OPTIONS.map((o) => (
            <button
              key={o.format}
              type="button"
              role="menuitem"
              className="pop-item"
              onClick={() => {
                buttonRef.current?.focus();
                setOpen(false);
                run(o.format);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
